// src/chatGPT/hooks/useStreamingChat.js - Enhanced for Multi-Agent LangGraph Streaming

import { useState, useContext, useRef, useCallback, useEffect } from "react";
import { ProductsContext } from "../../context/products.context";
import { projectAuth, db } from "../../firebase/config";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";

// Generate unique IDs for messages and chats
function generateChatId() {
  return `chat_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

function generateMessageId() {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

export default function useStreamingChat() {
  const {
    loading,
    setLoading,
    assistantContext,
    currentChatId,
    setCurrentChatId,
    visibleChatId,
    setVisibleChatId,
    getMessagesForChat,
    setMessagesForChat,
    getArtifactsForChat,
    setArtifactsForChat
  } = useContext(ProductsContext);

  // Track current progress messages
  const [progressMessage, setProgressMessage] = useState("");
  const [toolCallsInProgress, setToolCallsInProgress] = useState(0);

  // Chat session management - use context's currentChatId or generate new one
  const [chatId, setChatId] = useState(() => currentChatId || generateChatId());
  const [threadId, setThreadId] = useState(null);
  const abortControllerRef = useRef(null);

  // Request ID to guard against stale stream updates
  const activeReqRef = useRef(0);

  // Track in-flight tools
  const inFlightTools = useRef(new Set());

  // Token tracking
  const [streamingTokens, setStreamingTokens] = useState(0);
  const [streamingStartTime, setStreamingStartTime] = useState(null);

  // Sync chatId with context on init
  useEffect(() => {
    if (chatId && chatId !== currentChatId) {
      setCurrentChatId(chatId);
    }
    // Also set visible if not already set
    if (chatId && !visibleChatId) {
      setVisibleChatId(chatId);
    }
  }, [chatId, currentChatId, visibleChatId, setCurrentChatId, setVisibleChatId]);

  // Sync chatId to visibleChatId when user switches chats (GPT fix #3)
  useEffect(() => {
    if (visibleChatId && visibleChatId !== chatId && !loading) {
      // User switched to a different chat, sync our chatId
      setChatId(visibleChatId);
    }
  }, [visibleChatId, chatId, loading]);

  // Get messages for the current streaming chat
  const messages = getMessagesForChat(chatId);
  const artifacts = getArtifactsForChat(chatId);

  // Wrapper to set messages for the streaming chat (not the visible one)
  const setMessages = useCallback((newMessages) => {
    setMessagesForChat(chatId, newMessages);
  }, [chatId, setMessagesForChat]);

  // Wrapper to set artifacts for the streaming chat
  const setArtifacts = useCallback((newArtifacts) => {
    setArtifactsForChat(chatId, newArtifacts);
  }, [chatId, setArtifactsForChat]);

  // Update message by ID instead of using prev.at(-1) (GPT fix #1)
  const updateMessageById = useCallback((id, updater) => {
    setMessages(prev => prev.map(m => {
      if (m.id !== id) return m;
      return typeof updater === "function" ? updater(m) : { ...m, ...updater };
    }));
  }, [setMessages]);

  /* --- Main send function with enhanced streaming --- */
  const send = useCallback(async (userText) => {
    if (!userText.trim()) return;

    // Block multi-send while loading (GPT fix #4)
    if (loading) return;

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Increment request ID to invalidate stale stream updates (GPT fix #2)
    const reqId = ++activeReqRef.current;

    // Create new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Generate IDs for this request's messages
    const userMsgId = generateMessageId();
    const assistantMsgId = generateMessageId();

    // Add user message and assistant stub together
    setMessages((m) => [
      ...m,
      {
        id: userMsgId,
        role: "user",
        text: userText,
        done: true
      },
      {
        id: assistantMsgId,
        role: "assistant",
        text: "",
        done: false,
        progressMessages: [],
        toolCalls: 0
      }
    ]);

    // Now switch to viewing this chat (after messages are queued)
    setVisibleChatId(chatId);

    setLoading(true);
    setProgressMessage("");
    setToolCallsInProgress(0);
    inFlightTools.current.clear();

    // Reset token tracking
    setStreamingTokens(0);
    setStreamingStartTime(Date.now());

    // Safe update helpers that check request ID (GPT fix #2)
    const safeUpdateAssistant = (updater) => {
      if (activeReqRef.current !== reqId) return;
      updateMessageById(assistantMsgId, updater);
    };

    const safeAppend = (chunk) => {
      if (activeReqRef.current !== reqId) return;
      updateMessageById(assistantMsgId, (m) => ({
        ...m,
        text: (m.text || "") + chunk
      }));
    };

    const safeMarkDone = (extra = {}) => {
      if (activeReqRef.current !== reqId) return;
      updateMessageById(assistantMsgId, {
        done: true,
        progressMessages: [],
        ...extra
      });
    };

    let reader;
    let completed = false;

    try {
      // Get uid from Firebase Auth or context
      const currentUser = projectAuth.currentUser;
      const uid = assistantContext?.userProfile?.uid || currentUser?.uid || '';

      if (!uid) {
        throw new Error('User not authenticated. Please sign in.');
      }

      // Prepare request body for conference assistant endpoint
      const requestBody = {
        prompt: userText,
        context: {
          district: assistantContext?.district || {},
          userProfile: assistantContext?.userProfile || {}
        },
        chatId,
        uid
      };

      // Get auth token
      let authToken = null;
      try {
        if (currentUser) {
          authToken = await currentUser.getIdToken();
        }
      } catch (authError) {
        // Failed to get auth token
      }

      // Add to headers
      const headers = {
        "Content-Type": "application/json"
      };
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }
      // "https://testsingleagentstream-5wyy33ctoa-wl.a.run.app",
//"https://waassistantstream-5wyy33ctoa-wl.a.run.app"
      const res = await fetch(
        "https://testsingleagentstream-5wyy33ctoa-wl.a.run.app",
        {
          method: "POST",
          headers: headers,
          body: JSON.stringify(requestBody),
          signal: abortController.signal,
          cache: 'no-store'
        }
      );

      if (!res.ok || !res.body) {
        let errorMessage = `HTTP ${res.status}`;
        try {
          const errorData = await res.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          console.error('[useStreamingChat] Server error:', errorData);
        } catch (e) {
          console.error('[useStreamingChat] Failed to parse error response');
        }
        throw new Error(errorMessage);
      }

      // Process the enhanced stream
      reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let isFirstContent = true;

      // SSE parsing helpers
      const EVENT_SEP = /\r?\n\r?\n/;
      const LINE_SEP = /\r?\n/;

      /* Enhanced stream processing loop */
      while (true) {
        // Check if this request is stale before each iteration
        if (activeReqRef.current !== reqId) {
          break;
        }

        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split(EVENT_SEP);
        buffer = parts.pop() || '';

        for (const part of parts) {
          // Check staleness in inner loop too
          if (activeReqRef.current !== reqId) break;

          // Collect all data: lines and join them (SSE spec)
          const dataStr = part
            .split(LINE_SEP)
            .filter(l => l.startsWith('data:'))
            .map(l => l.slice(5).trimStart())
            .join('\n');

          if (!dataStr) continue;

          // Handle [DONE] message from LangGraph
          if (dataStr === '[DONE]') {
            safeMarkDone();
            setLoading(false);
            completed = true;
            continue;
          }

          let payload;
          try {
            payload = JSON.parse(dataStr);
          } catch (e) {
            continue;
          }

          // Handle different types of streamed data
          if (payload.error) {
            const errorType = payload.errorType || 'unknown';
            const errorMessage = errorType === 'tool'
              ? `\n\n⚠️ Tool Error: ${payload.error}`
              : `\n\n❌ Error: ${payload.error}`;

            safeAppend(errorMessage);
            safeMarkDone();
            setLoading(false);
            completed = true;
            break;
          }

          // Handle multi-agent LangGraph streaming events
          if (payload.type === 'start') {
            setProgressMessage(payload.message || 'Starting analysis...');
          } else if (payload.type === 'parallel') {
            setProgressMessage(`Running in parallel: ${payload.nodes?.join(' and ') || payload.message}`);
          } else if (payload.type === 'status') {
            setProgressMessage(payload.message || `Processing ${payload.node}...`);
          } else if (payload.type === 'token_count' || payload.type === 'token_update') {
            setStreamingTokens(payload.totalTokens || 0);
          } else if (payload.type === 'token_count_final') {
            setStreamingTokens(payload.totalTokens || 0);
            safeUpdateAssistant(prev => ({
              ...prev,
              tokenCount: {
                input: payload.inputTokens,
                output: payload.outputTokens,
                total: payload.totalTokens,
                timestamp: new Date().toISOString()
              }
            }));
          } else if (payload.type === 'content') {
            if (isFirstContent) {
              setProgressMessage('');
              isFirstContent = false;
            }
            if (payload.node === 'final_assembly' || payload.complete) {
              safeAppend(payload.content);
            }
          } else if (payload.type === 'title_generated') {
            // Handle title generation from backend - save to Firestore
            if (payload.title && payload.chatId) {
              const currentUser = projectAuth.currentUser;
              if (currentUser?.uid) {
                const chatRef = doc(db, 'users', currentUser.uid, 'chats', payload.chatId);
                updateDoc(chatRef, {
                  title: payload.title,
                  updatedAt: serverTimestamp()
                }).catch(err => {
                  console.error('[useStreamingChat] Failed to save generated title:', err);
                });
              }
            }
          } else if (payload.type === 'complete') {
            // Extract and store artifacts from hierarchical system
            if (payload.artifacts && Array.isArray(payload.artifacts)) {
              const mappedArtifacts = payload.artifacts.map((artifact) => ({
                identifier: artifact.id || artifact.identifier,
                artType: artifact.type || artifact.artType,
                title: artifact.title,
                data: artifact.data
              }));
              setArtifacts(mappedArtifacts);
            }

            // Store metadata with final token counts
            if (payload.metadata) {
              safeUpdateAssistant(prev => ({
                ...prev,
                done: true,
                tokenCount: {
                  input: payload.metadata.inputTokens || 0,
                  output: payload.metadata.outputTokens || 0,
                  total: payload.metadata.totalTokens || 0,
                  timestamp: new Date().toISOString()
                }
              }));
            } else {
              safeMarkDone();
            }

            setProgressMessage('');
            setLoading(false);
            completed = true;
          } else if (payload.content) {
            // Handle standard token streaming (fallback)
            if (isFirstContent) {
              setProgressMessage('');
              isFirstContent = false;
            }
            safeAppend(payload.content);
          }

          // Handle progress updates with unique tracking
          if (payload.progress) {
            const progressId = payload.progressId || payload.progress;

            if (!inFlightTools.current.has(progressId)) {
              inFlightTools.current.add(progressId);
            }

            setProgressMessage(payload.progress);
            safeUpdateAssistant(prev => ({
              ...prev,
              progressMessages: [...(prev.progressMessages || []), payload.progress],
              toolCalls: inFlightTools.current.size
            }));
            setToolCallsInProgress(inFlightTools.current.size);
          }

          // Handle fine-grained tool streaming events
          if (payload.type === 'tool_start') {
            const toolId = payload.toolId || payload.toolName;
            if (toolId && !inFlightTools.current.has(toolId)) {
              inFlightTools.current.add(toolId);
            }
            setProgressMessage(payload.progress || `Starting ${payload.toolName}...`);
            setToolCallsInProgress(inFlightTools.current.size);
          }

          if (payload.type === 'tool_progress') {
            setProgressMessage(payload.progress || `Processing ${payload.toolName}...`);
          }

          if (payload.type === 'tool_complete') {
            const toolId = payload.toolId || payload.toolName;
            if (toolId && inFlightTools.current.has(toolId)) {
              inFlightTools.current.delete(toolId);
            }
            setProgressMessage(payload.progress || `✓ ${payload.toolName} completed`);
            setToolCallsInProgress(inFlightTools.current.size);
          }

          // Handle artifacts inline
          if (payload.artifact) {
            const artifactTag = `\n<artifact identifier="${payload.artifact.identifier}" type="${payload.artifact.artType}" title="${payload.artifact.title}">${JSON.stringify(payload.artifact.data)}</artifact>\n`;
            safeAppend(artifactTag);
          }

          // Handle completion (fallback)
          if (payload.done) {
            safeMarkDone();
            setLoading(false);
            setProgressMessage('');
            setToolCallsInProgress(0);
            inFlightTools.current.clear();
            completed = true;
            break;
          }
        }
      }

      // Ensure we mark as done if we exit the loop
      if (!completed && activeReqRef.current === reqId) {
        safeMarkDone();
        setLoading(false);
        setProgressMessage("");
        setToolCallsInProgress(0);
        inFlightTools.current.clear();
        setStreamingStartTime(null);
      }

    } catch (err) {
      // Check if it was an abort
      if (err?.name === 'AbortError') {
        try {
          reader?.releaseLock?.();
        } catch {}

        if (activeReqRef.current === reqId) {
          safeAppend('\n\n*[Request cancelled]*');
          safeMarkDone();
        }
      } else {
        // Streaming error - only update if this is still the active request
        if (activeReqRef.current === reqId) {
          updateMessageById(assistantMsgId, {
            text: `Sorry, I encountered an error: ${err.message || 'Unknown error'}. Please try again.`,
            done: true
          });
        }
      }

      setLoading(false);
      setProgressMessage("");
      setToolCallsInProgress(0);
      inFlightTools.current.clear();
    } finally {
      // Clean up abort controller
      abortControllerRef.current = null;
    }
  }, [
    setMessages,
    setLoading,
    setVisibleChatId,
    setArtifacts,
    updateMessageById,
    chatId,
    assistantContext,
    loading
  ]);

  // New chat function
  const newChat = useCallback(() => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Increment request ID to invalidate any pending updates
    activeReqRef.current++;

    const newChatId = generateChatId();
    setChatId(newChatId);
    setCurrentChatId(newChatId);
    setVisibleChatId(newChatId);
    setThreadId(null);

    // Clear stored threadId from localStorage
    if (typeof window !== 'undefined' && chatId) {
      window.localStorage.removeItem(`thread_${chatId}`);
    }

    inFlightTools.current.clear();
  }, [chatId, setCurrentChatId, setVisibleChatId]);

  // Cancel streaming function
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  return {
    messages,
    send,
    loading,
    progressMessage,
    toolCallsInProgress,
    artifacts,
    setArtifacts,
    chatId,
    setChatId,
    threadId,
    setThreadId,
    newChat,
    cancel,
    streamingTokens,
    streamingStartTime
  };
}
